// MasterCsvImportBar.tsx — "Import CSV" / template / export for the Payers
// and Service master tabs (wireframe S9's "Import CSV" on both cards;
// Insurance Claims plan 1.2/2.1). The server import is create-only: a code
// that already exists, or appears twice in the file, comes back as a
// row-level error (utils/masterCsvImport.js), and every such row is listed
// here with its message so the admin can fix the file and re-import just
// those rows.
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { bulkImportMaster, downloadMasterCsv, type ClaimsMaster, type MasterCsvImportResult } from '../api';

export function MasterCsvImportBar({ master, onImported }: { master: ClaimsMaster; onImported: () => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<MasterCsvImportResult | null>(null);
  const [error, setError] = useState('');

  const pick = async (file: File) => {
    setBusy(true); setError(''); setResult(null);
    try {
      const r = await bulkImportMaster(master, file);
      setResult(r);
      if (r.created > 0) onImported();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setBusy(false);
  };
  const download = (kind: 'import-template' | 'export') =>
    downloadMasterCsv(master, kind).catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));

  const failures = result?.results.filter((r) => r.status === 'error') ?? [];
  return (
    <div className="mb-3">
      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={inputRef} type="file" accept=".csv,text/csv" className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) pick(f); e.target.value = ''; }}
        />
        <Button size="sm" variant="ghost" loading={busy} onClick={() => inputRef.current?.click()}>⬆ Import CSV</Button>
        <Button size="sm" variant="ghost" onClick={() => download('import-template')}>Template</Button>
        <Button size="sm" variant="ghost" onClick={() => download('export')}>⬇ Export CSV</Button>
        <span className="text-[11px] text-ink-muted">Import adds new codes only — edit an existing row on screen.</span>
      </div>
      {error && <div className="mt-2 rounded-[var(--radius)] bg-danger-soft px-3 py-2 text-xs text-danger-ink">{error}</div>}
      {result && (
        <div className="mt-2 rounded-[var(--radius)] border border-border bg-muted px-3 py-2 text-[12px]">
          <div className="flex flex-wrap items-center gap-2">
            <span>{result.processed} row{result.processed === 1 ? '' : 's'} read:</span>
            <Badge variant="success">{result.created} created</Badge>
            {result.failed > 0 && <Badge variant="danger">{result.failed} failed</Badge>}
            <button type="button" className="ml-auto text-[11px] text-link underline" onClick={() => setResult(null)}>Dismiss</button>
          </div>
          {failures.length > 0 && (
            <ul className="mt-1.5 max-h-40 list-inside list-disc overflow-auto text-danger-ink">
              {failures.map((f) => <li key={f.row}>Row {f.row}{f.code ? ` (${f.code})` : ''}: {f.message}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
